/**
 * AnyClip — 상태 표시 컴포넌트 (로딩/완료/에러)
 */

import React from 'react';
import { useApp, ACTIONS } from '../context/AppContext';
import { useI18n } from '../context/I18nContext';
import './StatusDisplay.css';

function StatusDisplay() {
  const { state, dispatch } = useApp();
  const { t } = useI18n();
  const { status, streamUrl, type, mediaUrls } = state;

  const [isDownloading, setIsDownloading] = React.useState(false);

  const downloadSingleUrl = async (urlToDownload, filename) => {
    if (window.Telegram?.WebApp?.downloadFile) {
      return new Promise((resolve) => {
        const onDownloadDone = () => {
          if (window.Telegram?.WebApp?.offEvent) {
            window.Telegram.WebApp.offEvent('downloadFileDone', onDownloadDone);
          }
          resolve();
        };
        if (window.Telegram?.WebApp?.onEvent) {
          window.Telegram.WebApp.onEvent('downloadFileDone', onDownloadDone);
        }
        window.Telegram.WebApp.downloadFile({ url: urlToDownload, file_name: filename });
        // Fallback resolve after 5s if event fails
        setTimeout(resolve, 5000);
      });
    }

    try {
      const response = await fetch(urlToDownload);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      if (window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(urlToDownload);
      } else {
        const a = document.createElement('a');
        a.href = urlToDownload;
        a.download = filename;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }
  };

  const triggerDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    try {
      if (type === 'carousel' || type === 'image') {
        const urls = mediaUrls?.length > 0 ? mediaUrls : [streamUrl];
        for (let i = 0; i < urls.length; i++) {
          const ext = urls[i].includes('.webp') ? '.webp' : '.jpg';
          await downloadSingleUrl(urls[i], `anyclip_image_${i + 1}${ext}`);
          // Add a small delay between downloads to prevent browser blocking
          if (i < urls.length - 1) await new Promise(r => setTimeout(r, 800));
        }
      } else {
        if (!streamUrl) return;
        await downloadSingleUrl(streamUrl, 'anyclip_video.mp4');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReset = () => dispatch({ type: ACTIONS.RESET });

  if (status === 'idle') return null;

  if (status === 'loading') {
    return (
      <div className="status-display status-display--loading" role="status" aria-live="polite">
        <div className="spinner-ring">
          <div /><div /><div /><div />
        </div>
        <p className="status-text">{t('status_extracting')}</p>
        <p className="status-sub">{t('status_extracting_sub')}</p>
      </div>
    );
  }

  if (status === 'success') {
    const isGallery = type === 'carousel' || type === 'image';
    const urls = mediaUrls?.length > 0 ? mediaUrls : (streamUrl ? [streamUrl] : []);

    return (
      <div className="status-display status-display--success" role="status">
        <div className="success-icon">✅</div>
        <p className="status-text">{t('status_success')}</p>
        <p className="status-sub">
          {isGallery ? t('status_success_gallery').replace('{n}', urls.length) : t('status_success_sub')}
        </p>

        {isGallery && urls.length > 0 && (
          <div className="gallery-preview">
            {urls.slice(0, 4).map((imgUrl, idx) => (
              <div key={idx} className="gallery-item">
                <img src={imgUrl} alt={`Preview ${idx + 1}`} loading="lazy" />
              </div>
            ))}
            {urls.length > 4 && (
              <div className="gallery-item gallery-more">
                +{urls.length - 4}
              </div>
            )}
          </div>
        )}

        <button
          className="download-trigger-btn"
          onClick={triggerDownload}
          id="trigger-download-btn"
          disabled={isDownloading}
        >
          {isDownloading ? t('btn_saving') : (isGallery ? t('btn_save_all') : t('btn_save'))}
        </button>
        <button className="reset-btn" onClick={handleReset} id="reset-btn" disabled={isDownloading}>
          {t('btn_reset')}
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="status-display status-display--error" role="alert">
        <div className="error-icon">⚠️</div>
        <p className="status-text">{t('status_error')}</p>
        <p className="status-sub">{state.errorMsg || t('error_unknown')}</p>
        <button className="reset-btn" onClick={handleReset} id="error-reset-btn">
          {t('btn_retry')}
        </button>
      </div>
    );
  }

  return null;
}

export default StatusDisplay;
